// ── eventStateCertainty — how much to believe this event's state ────
//
// #481 (leaf of #469). `liveDisplayState` answers "what should the card
// SAY about this game". This module answers the different question a
// trader needs before leaving an order resting or firing a new one:
// "how much can I believe that answer right now?"
//
// The two are deliberately separate. A card can honestly render UNKNOWN
// and still be a perfectly calm surface; an order ticket looking at the
// same UNKNOWN needs to be told, in words, that nothing confirms the
// event's state. Folding this into `resolveLiveDisplayState` would have
// meant one function returning both a display state and a trust level —
// the chip's four values would have had to grow to carry uncertainty
// they were explicitly designed NOT to carry.
//
// ── A clock can create doubt, never authority ───────────────────────
// The resolver takes no clock, exactly as `liveDisplayState` takes none.
// Instead marketStore supplies both the derived row state and the reason
// for it. A future scheduled start may vouch for `pre`; a venue-terminal
// status may vouch for `final`. Clock-started live rows, clock-coerced
// finals, and rows without a known start remain heuristic/unknown and
// therefore cannot silence the warning on their own.
//
// ── The producer slot ───────────────────────────────────────────────
// `canonical` is the normalized per-event state already returned by OG
// Hydra's batched game-state route. Value Dashboard and Position Tracker
// now use the same exact-key record as their score/LIVE display. A missing,
// malformed, or stale record still falls through to doubt; this does not
// invent state from a scheduled clock.
//
// ── What stays in #469 ──────────────────────────────────────────────
// This path remains presentation only. The autonomous fail-closed placement
// policy ("do not place when the required authority is unknown or stale")
// remains separate work in #469. Nothing in this module or its callers
// cancels, places, reprices, resizes or replaces an order.

import { isConfirmedFinal } from './periodLabel';
import type { CanonicalGameStateInput, RowGameState } from './liveDisplayState';
import { trustedCanonicalState } from './liveDisplayState';

/**
 * How much an event-state answer can be believed.
 *
 *   trusted        Nothing disagrees. Render no warning.
 *   unknown        No current authoritative source can confirm what the
 *                  event is doing.
 *   stale          A canonical source exists but its feed has been
 *                  failing long enough that its snapshot is not current.
 *   contradictory  The canonical source and the market heuristic
 *                  disagree about where the event is in its lifecycle.
 */
export type EventStateCertainty = 'trusted' | 'unknown' | 'stale' | 'contradictory';

/** Why the row currently claims its lifecycle state. */
export type EventStateAuthority = 'venue' | 'schedule' | 'heuristic' | 'unknown';

/**
 * What the canonical snapshot is worth right now.
 *
 *   current  A snapshot landed recently enough to reason from.
 *   stale    A snapshot exists but its feed has been failing past the
 *            bounded window — decayed, not absent.
 *   absent   No snapshot has ever landed here. A cold start, a poll that
 *            has only ever failed, or a surface nothing polls for.
 *
 * `absent` is distinct from `stale` on purpose: "the feed has been
 * failing and its snapshot is too old to trust" is a false statement
 * about a feed that never returned anything, and a warning that
 * misdescribes its own cause teaches the operator to discount it.
 */
export type FeedSnapshotState = 'current' | 'stale' | 'absent';

export interface EventStateCertaintyInput {
  /**
   * Normalized canonical event state, when a source G1 trusts exists.
   * OG Hydra's current game-state slot. Untrusted records (a
   * scoreless `statusId: 3`, an unrecognized code) are dropped by
   * `trustedCanonicalState` and read the same as absent.
   */
  canonical: CanonicalGameStateInput | null | undefined;
  /** marketStore's `GameRow.gameState` heuristic. A doubt signal only. */
  rowGameState: RowGameState;
  /**
   * Provenance for `rowGameState`. A venue terminal status can vouch for a
   * final row; a future scheduled start can vouch only for pregame. Clock-
   * coerced finals and clock-started live rows remain heuristic and therefore
   * cannot clear the warning by themselves.
   */
  rowAuthority?: EventStateAuthority;
  /**
   * How much the canonical snapshot is worth, decided by the caller from
   * `isGameStateFeedStale` and whether a snapshot ever landed. Passed in
   * rather than computed here so this module keeps taking no clock and
   * no store — the discipline `resolveLiveDisplayState` follows.
   */
  feed: FeedSnapshotState;
}

/**
 * Resolve how much this event's state can be believed.
 *
 * Precedence, and why:
 *
 *  1. Disagreement first — but only against a CURRENT snapshot. One
 *     authority rule, applied once: a snapshot too old to vouch for a
 *     state is also too old to convict one. A record frozen at
 *     "scheduled" while the clock rolls past the start is the signature
 *     of a dead feed, not evidence a schedule moved, and `stale` is the
 *     accurate thing to tell the operator in that case.
 *     Three disagreements count, in both directions across the start
 *     boundary and across the whole lifecycle:
 *       • canonical scheduled vs a market the clock calls live — the
 *         schedule moved, or a postponement nobody published;
 *       • canonical live vs a market still reading pregame — the start
 *         came early;
 *       • canonical final vs a market that says NOT STARTED — a
 *         doubleheader key collision, a wrong game mapping, or a
 *         schedule the venue never corrected. Silence is the worst of
 *         the four answers when a source positively claims the event is
 *         over.
 *  2. Provenance can make a row authoritative without a canonical snapshot:
 *     a future schedule vouches for `pre`, and a venue-terminal status
 *     vouches for `final`. Heuristic or unknown provenance cannot.
 *  3. When row provenance is insufficient, a decayed feed is `stale` and outranks
 *     `unknown`. Both mean "cannot confirm", but stale names a defect
 *     the operator can act on (the feed is down) rather than a gap.
 *  4. A current canonical that agrees is `trusted`; anything else —
 *     including a snapshot that never arrived — is `unknown`.
 */
export function resolveEventStateCertainty({
  canonical,
  rowGameState,
  rowAuthority = 'unknown',
  feed,
}: EventStateCertaintyInput): EventStateCertainty {
  const trusted = trustedCanonicalState(canonical);
  const canonicalLive = trusted?.statusId === 2;
  const canonicalScheduled = trusted?.statusId === 1;
  const canonicalFinal = isConfirmedFinal(trusted);

  if (feed === 'current') {
    if (canonicalScheduled && rowGameState === 'live') return 'contradictory';
    if (canonicalLive && rowGameState === 'pre') return 'contradictory';
    if (canonicalFinal && rowGameState === 'pre') return 'contradictory';
  }

  if (rowGameState === 'pre') {
    if (rowAuthority === 'schedule') return 'trusted';
    if (feed === 'stale') return 'stale';
    if (feed === 'current' && canonicalScheduled) return 'trusted';
    return 'unknown';
  }

  if (rowGameState === 'final') {
    if (rowAuthority === 'venue') return 'trusted';
    if (feed === 'stale') return 'stale';
    if (feed === 'current' && canonicalFinal) return 'trusted';
    return 'unknown';
  }

  if (feed === 'stale') return 'stale';
  // A canonical confirmed final on a row the heuristic still calls live
  // is not a contradiction — `deriveGameState` keeps a row live until a
  // venue closes the book, so the feed simply knows first. The card
  // already renders FINAL from it.
  if (feed === 'current' && (canonicalLive || canonicalFinal)) return 'trusted';
  return 'unknown';
}
